/* eslint-disable no-magic-numbers */
import React, { useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import get from 'lodash/get';
import classNames from 'classnames';

import { toastError } from '@futrli/ui-components';
import { Logger, REQUEST_METHODS } from '@futrli/shared-services';

import { Data } from '../../../utils/table-filters/filter-table-types';
import * as styles from '../virtualized-list.scss';
import { downloadFile, viewFile } from './utils';
import { makeRequestWithResponse } from '../../../core/api/shared';
import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

interface Props {
  rowData?: Data;
  rowIndex: number;
}

export const SentPdfTableRow = ({ rowData, rowIndex }: Props) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);

  const onDownloadClick = async () => {
    try {
      setIsDownloading(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await downloadFile(downloadURL.url, `${rowData.name}.pdf`);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsDownloading(false);
    }
  };

  const onViewClick = async () => {
    try {
      setIsViewing(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { view_url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await viewFile(downloadURL.view_url);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsViewing(false);
    }
  };

  if (!rowData) {
    return null;
  }

  return (
    <>
      <div
        role="row"
        key={rowData.id}
        className={clsx(styles.virtualizedListRow)}
        style={{ backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : 'transparent', position: 'relative' }}
      >
        <div
          role="cell"
          className={clsx(
            styles.virtualizedListCell,
            styles.flex3,
            styles.documentLibraryTableFont,
            styles.svgContainer
          )}
          style={{ minWidth: 0, border: 'none' }}
        >
          <Icon type="file_pdf" />
          <span className={styles.textOverflow}>{`${rowData.name}.pdf`}</span>
        </div>
        {rowData.next_generate_at ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{format(rowData.next_generate_at, 'dd/MM/yyyy')}</span>
          </div>
        ) : null}
        {rowData.time ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{rowData.time}</span>
          </div>
        ) : null}
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
          style={{ minWidth: 0, border: 'none' }}
        >
          <span>{rowData.period}</span>
        </div>
        {!rowData.users ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex5, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <div
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: 'calc(100% - 62px)'
              }}
            >
              {rowData.users}
            </div>
          </div>
        ) : (
          <Tooltip message={rowData.users}>
            <div
              role="cell"
              className={clsx(styles.virtualizedListCell, styles.flex5, styles.documentLibraryTableFont)}
              style={{ minWidth: 0, border: 'none' }}
            >
              <div
                style={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  width: 'calc(100% - 62px)'
                }}
              >
                {rowData.users}
              </div>
            </div>
          </Tooltip>
        )}

        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
          style={{
            position: 'absolute',
            right: 0,
            display: 'flex',
            backgroundColor: '#f6f7fa',
            justifyContent: 'center',
            paddingRight: 0
          }}
        >
          <div
            className={classNames(styles.iconContainer, {
              [styles.disabledIcon]: isViewing
            })}
            onClick={onViewClick}
            style={{ marginRight: 12 }}
          >
            <Icon type="view" />
          </div>
          <div
            className={classNames(styles.iconContainer, {
              [styles.disabledIcon]: isDownloading
            })}
            onClick={onDownloadClick}
          >
            <Icon type="download" />
          </div>
        </div>
      </div>
    </>
  );
};
